import { useEffect, useRef, useState, useMemo } from 'react';
import { Canvas, useLoader, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { SVGLoader } from 'three-stdlib';
import { Environment, ContactShadows, Center, OrbitControls } from '@react-three/drei';

// 1. PARTÍCULAS ENVOLVENTES REFORZADAS (Fondo y Frente)
function GlobalParticles({ count = 4500 }) {
  const pointsRef = useRef<THREE.Points>(null);
  
  const particles = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 50;     
      pos[i * 3 + 1] = (Math.random() - 0.5) * 30; 
      pos[i * 3 + 2] = (Math.random() - 0.5) * 30; 
    }
    return pos;
  }, [count]);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = state.clock.elapsedTime * 0.01;
      pointsRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.1) * 0.05;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={particles.length / 3} array={particles} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial 
        size={0.04} 
        color="#00C2FF" 
        transparent 
        opacity={0.4} 
        sizeAttenuation 
        blending={THREE.AdditiveBlending} 
      />
    </points>
  );
}

// 2. LOGO 3D CON ANIMACIÓN SUAVE
function LogoScene({ isLoaded, isMobile }: { isLoaded: boolean; isMobile: boolean }) {
  const svgData = useLoader(SVGLoader, '/code.svg');
  const groupRef = useRef<THREE.Group>(null);
  const floatRef = useRef<THREE.Group>(null);
  const mousePos = useRef({ x: 0, y: 0 });
  
  const shapes = useMemo(() => {
    return svgData.paths.flatMap((path) => {
      const pathShapes = SVGLoader.createShapes(path);
      return pathShapes.map((shape) => ({ shape, color: path.color }));
    });
  }, [svgData]);

  useEffect(() => {
    if (isMobile) return;
    const handleMouseMove = (e: MouseEvent) => {
      mousePos.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mousePos.current.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

    const light1Ref = useRef<THREE.PointLight>(null);
  const light2Ref = useRef<THREE.PointLight>(null);
  const light3Ref = useRef<THREE.PointLight>(null);

  // Luces que orbitan el logo creando reflejos dinámicos en el metal
  useFrame((state) => {
    if (!groupRef.current || !floatRef.current) return;
    const targetScale = isLoaded ? 1.15 : 0;
    groupRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.05);

    const t = state.clock.elapsedTime;
    floatRef.current.position.y = Math.sin(t * 1.5) * 0.12;
    floatRef.current.position.x = Math.sin(t * 0.7) * 0.03;

    if (isMobile) {
      // En móvil: oscilación suave sobre su propio eje Y (sin interacción)
      groupRef.current.rotation.y = Math.sin(t * 0.5) * 0.35;
    } else {
      // En desktop: oscilación suave sobre eje Y + leve inclinación con el mouse
      groupRef.current.rotation.y = Math.sin(t * 0.5) * 0.35;
      const targetRotX = -mousePos.current.y * 0.03;
      groupRef.current.rotation.x = THREE.MathUtils.lerp(groupRef.current.rotation.x, targetRotX, 0.05);
    }

    // Orbitar luz 1 (cian) — órbita horizontal cercana
    if (light1Ref.current) {
      light1Ref.current.position.x = Math.sin(t * 0.6) * 4;
      light1Ref.current.position.z = Math.cos(t * 0.6) * 4 + 2;
      light1Ref.current.position.y = 2;
    }
    // Orbitar luz 2 (blanca) — órbita vertical cercana
    if (light2Ref.current) {
      light2Ref.current.position.y = Math.sin(t * 0.4) * 3;
      light2Ref.current.position.z = Math.cos(t * 0.4) * 3 + 3;
      light2Ref.current.position.x = -2;
    }
    // Orbitar luz 3 (azul) — órbita diagonal cercana
    if (light3Ref.current) {
      light3Ref.current.position.x = Math.cos(t * 0.5) * 3;
      light3Ref.current.position.y = Math.sin(t * 0.5) * 3;
      light3Ref.current.position.z = 3;
    }
  });

  return (
    <>
      {/* Luz ambiente — base visible */}
      <ambientLight intensity={0.8} />

      {/* Luces orbitantes — reflejos dinámicos del metal */}
      <pointLight ref={light1Ref} intensity={50} color="#1aaed4" />
      <pointLight ref={light2Ref} intensity={40} color="#4da8ff" />
      <pointLight ref={light3Ref} intensity={38} color="#0558b0" />

      {/* Spot fijo desde arriba — highlight estático en la cima */}
      <spotLight position={[0, 6, 3]} angle={0.3} penumbra={0.2} intensity={35} color="#4da8ff" castShadow />
      {/* Contraluz trasero — separa el logo del fondo oscuro */}
      <pointLight position={[0, 0, -3]} intensity={20} color="#1aaed4" />

      <Center top position={[0, -0.5, 0]}>
        <group ref={groupRef}>
          <group ref={floatRef}>
            <group scale={0.012} rotation={[Math.PI, 0, 0]}>
              {shapes.map((item, index) => (
                <mesh key={index} castShadow receiveShadow>
                  <extrudeGeometry args={[item.shape, { depth: 20, bevelEnabled: true, bevelThickness: 3.5, bevelSize: 2.5 }]} />
                  <meshStandardMaterial 
                    color={index % 2 === 0 ? "#0558b0" : "#1aaed4"}
                    emissive={index % 2 === 0 ? "#033d80" : "#0a6fa0"}
                    emissiveIntensity={0.5}
                    roughness={0.25}
                    metalness={0.5}
                    envMapIntensity={3}
                  />
                </mesh>
              ))}
            </group>
          </group>
        </group>
      </Center>
    </>
  );
}

// 3. COMPONENTE HERO FINAL
export default function Hero() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => { setIsLoaded(true); }, []);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#050608]">
      
      <style>{`
        @keyframes shimmer-flow {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes glow-pulse {
          0%, 100% { filter: drop-shadow(0 0 8px rgba(0, 194, 255, 0.3)); }
          50% { filter: drop-shadow(0 0 25px rgba(0, 194, 255, 0.6)) drop-shadow(0 0 50px rgba(26, 115, 232, 0.2)); }
        }
        @keyframes text-reveal {
          0% { opacity: 0; transform: translateY(20px); filter: blur(8px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        .shimmer-text {
          background: linear-gradient(
            90deg, 
            #00C2FF 0%, 
            #1A73E8 25%, 
            #00e5ff 50%, 
            #1A73E8 75%, 
            #00C2FF 100%
          );
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer-flow 4s linear infinite, glow-pulse 3s ease-in-out infinite;
        }
        .hero-text-reveal {
          animation: text-reveal 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .hero-text-reveal-delay {
          opacity: 0;
          animation: text-reveal 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards;
        }
        .hero-text-reveal-delay-2 {
          opacity: 0;
          animation: text-reveal 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.6s forwards;
        }
      `}</style>

      {/* FONDO DE PARTÍCULAS ENVOLVENTE */}
      <div className="absolute inset-0 z-0 pointer-events-none w-full h-full opacity-60">
        <Canvas camera={{ position: [0, 0, 15] }}>
          <GlobalParticles />
        </Canvas>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 relative z-10 w-full pt-20 sm:pt-24 lg:pt-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr,1fr] gap-8 lg:gap-12 items-center">
          
          {/* TEXTO CON CORRECCIÓN DE ALTURA Y EFECTO GLITCH */}
          <div className={`transition-all duration-1000 text-center lg:text-left ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-white mb-4 sm:mb-6 leading-[1.15] font-mono pb-4 sm:pb-6">
              <span className="hero-text-reveal inline-block">Transformamos</span> <br /> 
              <span className="hero-text-reveal-delay inline-block">Ideas en</span>
              <div className="relative inline-block w-full">
                <span className="hero-text-reveal-delay-2 block shimmer-text mt-2 py-2">
                  Realidad Digital
                </span>
              </div>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-xl mx-auto lg:mx-0 hero-text-reveal-delay-2">
              Desarrollamos software personalizado e inteligencia artificial para impulsar tu negocio. Somos expertos en desarrollo web, apps móviles, cloud computing y soluciones de IA para empresas en Colombia.
            </p>
          </div>

          {/* CONTENEDOR DEL LOGO 3D */}
          <div className="relative h-[350px] sm:h-[450px] md:h-[550px] lg:h-[700px] w-full mt-6 sm:mt-10 lg:mt-16">
            {/* Halo de fondo que da profundidad */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
              <div className="w-[60%] h-[60%] rounded-full bg-[#00C2FF]/10 blur-[80px]" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
              <div className="w-[40%] h-[40%] rounded-full bg-[#1A73E8]/15 blur-[60px]" />
            </div>
            <Canvas shadows camera={{ position: [0, 0, 10], fov: 32 }} className="relative z-10">
              <LogoScene isLoaded={isLoaded} isMobile={isMobile} />
              {!isMobile && (
                <OrbitControls
                  enableZoom={false}
                  enablePan={false}
                />
              )}
              <ContactShadows position={[0, -2.5, 0]} opacity={0.4} scale={15} blur={3} />
              <Environment preset="studio" />
            </Canvas>
          </div>
        </div>
      </div>
    </section>
  );
}